import { IonImg, IonLabel } from '@ionic/react';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Vectors from '@/assets/imgs/vectors.svg';
import { TABLE_SCROLL_EVENT, TABLE_SCROLL_EVENT_BOTTOM } from '@/constants';
import useMediaQuery, { Breakpoint } from '@/hooks/useMediaQuery';
import { DashboardColumns as DashboardColumnsEnum } from '@/interfaces/DashboardInterface/DashboardInterface';
import { getColWidths } from '@/redux/dashboard/dashboard';
import { OrderSortType, OrderType, SortType } from '@/redux/operation/operationsInterface';
import { storageService } from '@/services/storage';
import { RootState } from '@/store';
import { getDefaultDashboardColumns, resolveDashboardName } from '@/utils/dashboardColumnts';

import { DashboardColumnConfig } from '../DashboardTableDesktop';
import styles from './DasboardTableDesktopHead.module.scss';
import { desktop } from 'ionicons/icons';

interface DasboardTableDesktopHeadProps {
  orderByData: (orderBy: OrderSortType) => void;
  loadingData: boolean;
}

export default function DasboardTableDesktopHead({ orderByData, loadingData }: DasboardTableDesktopHeadProps) {
  const colWidths = useSelector(getColWidths);
  const tableHeadRef = useRef<HTMLDivElement>(null);
  const [cookieProposalReference] = useState(storageService.hasAGCode());
  const isTablet = useMediaQuery(Breakpoint.Tablet);
  const isDesktop = useMediaQuery(Breakpoint.Tablet);
  const isDesktopLarge = useMediaQuery(Breakpoint.DesktopLarge);
  const [isMobile, setIsMobile] = useState(!isTablet && !isDesktop);
  const [dashboardColumns, setDashboardColumns] = useState<DashboardColumnConfig[]>([]);
  const customDashboardColumns = useSelector((state: RootState) => state.dashboard.columns);
  const [currentOrderSort, setCurrentOrderSort] = useState<OrderSortType>();

  useEffect(() => {
    if (!tableHeadRef.current) return;
    const handleScroll = (event: Event) => {
      const target = event.target as HTMLDivElement;
      const customEvent = new CustomEvent(TABLE_SCROLL_EVENT, {
        detail: { scrollLeft: target.scrollLeft },
      });
      window.dispatchEvent(customEvent);
    };
    tableHeadRef.current.addEventListener('scroll', handleScroll);

    return () => {
      tableHeadRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, [tableHeadRef.current]);

  useEffect(() => {
    const handleTableScroll = (event: CustomEvent) => {
      if (tableHeadRef.current) {
        tableHeadRef.current.scrollLeft = event.detail.scrollLeft;
      }
    };

    window.addEventListener(TABLE_SCROLL_EVENT_BOTTOM, handleTableScroll as EventListener);

    return () => {
      window.removeEventListener(TABLE_SCROLL_EVENT_BOTTOM, handleTableScroll as EventListener);
    };
  }, []);

  useEffect(() => {
    setIsMobile(!isTablet && !isDesktop);
    if (isDesktopLarge || isTablet) {
      setDashboardColumns(customDashboardColumns.map((col) => ({ id: col })));
    } else {
      setDashboardColumns(getDefaultDashboardColumns().map((col) => ({ id: col })));
    }
  }, [isTablet, isDesktop, isDesktopLarge, customDashboardColumns]);

  const handleOrderClick = (data: OrderSortType) => {
    let cSort: OrderSortType = data;
    if (currentOrderSort?.order === data.order) {
      cSort = {
        order: data.order,
        sort:
          currentOrderSort.sort === SortType.ASC ? SortType.DESC : SortType.ASC,
      };
    } else {
      cSort = data;
    }
    orderByData(cSort);
    setCurrentOrderSort(cSort);
  };

  const renderColumnHeader = (column: DashboardColumnsEnum, index: number) => {
    switch (column) {
      case DashboardColumnsEnum.STATUS:
        return (
          <div className={styles.columnHeader}>
            <div className={styles.headerContent}>
              <IonLabel
                onClick={() => handleOrderClick({
                  order: OrderType.STATUS,
                  sort: SortType.ASC,
                })}
                style={{
                  maxWidth: colWidths[index],
                }}
                className={classNames(styles.generalTableTitle, styles.sortAvailable, styles.columnTruncate)}
              >
                {resolveDashboardName(column)}
              </IonLabel>
              <IonImg src={Vectors} />
            </div>
          </div>
        );
      case DashboardColumnsEnum.DNI_CIF:
        return (
          <div className={styles.columnHeader}>
            <div className={styles.headerContent}>
              <IonLabel
                style={{
                  maxWidth: colWidths[index],
                }}
                className={classNames(styles.generalTableTitle, styles.columnTruncate)}
              >
                {resolveDashboardName(column)}
              </IonLabel>
            </div>
          </div>
        );
      case DashboardColumnsEnum.AG_CODE:
        return cookieProposalReference && (isDesktop || isDesktopLarge || isTablet) ? (
          <div className={styles.columnHeader}>
            <div className={styles.headerContent}>
              <IonLabel
                style={{
                  maxWidth: colWidths[index],
                }}
                className={classNames(styles.generalTableTitle, styles.columnTruncate)}
              >
                {resolveDashboardName(column)}
              </IonLabel>
            </div>
          </div>
        ) : null;
      case DashboardColumnsEnum.CUSTOMER:
        return (
          <div className={styles.columnHeader}>
            <div className={styles.headerContent}>
              <IonLabel
                onClick={() => handleOrderClick({
                  order: OrderType.CUSTOMER,
                  sort: SortType.ASC,
                })}
                style={{
                  maxWidth: colWidths[index],
                }}
                className={classNames(styles.generalTableTitle, styles.sortAvailable, styles.columnTruncate)}
              >
                {resolveDashboardName(column)}
              </IonLabel>
              <IonImg src={Vectors} />
            </div>
          </div>
        );
      case DashboardColumnsEnum.DATE:
        return (
          <div className={styles.columnHeader}>
            <div className={styles.headerContent}>
              <IonLabel
                onClick={() => handleOrderClick({
                  order: OrderType.DATE,
                  sort: SortType.ASC,
                })}
                style={{
                  maxWidth: colWidths[index],
                }}
                className={classNames(styles.generalTableTitle, styles.sortAvailable, styles.columnTruncate)}
              >
                {resolveDashboardName(column)}
              </IonLabel>
              <IonImg src={Vectors} />
            </div>
          </div>
        );
      case DashboardColumnsEnum.AMOUNT:
        return (!isMobile || isDesktopLarge) ? (
          <div className={styles.columnHeader}>
            <div className={styles.headerContent}>
              <IonLabel
                onClick={() => handleOrderClick({
                  order: OrderType.AMOUNT,
                  sort: SortType.ASC,
                })}
                style={{
                  maxWidth: colWidths[index],
                }}
                className={classNames(styles.generalTableTitle, styles.sortAvailable, styles.columnTruncate)}
              >
                {resolveDashboardName(column)}
              </IonLabel>
              <IonImg src={Vectors} />
            </div>
          </div>
        ) : null;
      case DashboardColumnsEnum.EMAIL:
        return (
          <div className={styles.columnHeader}>
            <div className={styles.headerContent}>
              <IonLabel
                style={{
                  maxWidth: colWidths[index],
                }}
                className={classNames(styles.generalTableTitle, styles.columnTruncate)}
              >
                {resolveDashboardName(column)}
              </IonLabel>
              <IonImg src={Vectors} />
            </div>
          </div>
        );
      case DashboardColumnsEnum.OFFER:
        return (
          <div className={styles.columnHeader}>
            <div className={styles.headerContent}>
              <IonLabel
                style={{
                  maxWidth: colWidths[index],
                }}
                className={classNames(styles.generalTableTitle, styles.columnTruncate)}
              >
                {resolveDashboardName(column)}
              </IonLabel>
            </div>
          </div>
        );
      case DashboardColumnsEnum.PROPOSAL_TERM:
        return (
          <div className={styles.columnHeader}>
            <div className={styles.headerContent}>
              <IonLabel
                style={{
                  maxWidth: colWidths[index],
                }}
                className={classNames(styles.generalTableTitle, styles.columnTruncate)}
              >
                {resolveDashboardName(column)}
              </IonLabel>
            </div>
          </div>
        );
      case DashboardColumnsEnum.CART_ID:
        return (
          <div className={styles.columnHeader}>
            <div className={styles.headerContent}>
              <IonLabel
                style={{
                  maxWidth: colWidths[index],
                }}
                className={classNames(styles.generalTableTitle, styles.columnTruncate)}
              >
                {resolveDashboardName(column)}
              </IonLabel>
            </div>
          </div>
        );
      case DashboardColumnsEnum.PRODUCT:
        return (
          <div className={styles.columnHeader}>
            <div className={styles.headerContent}>
              <IonLabel
                style={{
                  maxWidth: colWidths[index],
                }}
                className={classNames(styles.generalTableTitle, styles.columnTruncate)}
              >
                {resolveDashboardName(column)}
              </IonLabel>
            </div>
          </div>
        );
      case DashboardColumnsEnum.APPLICATION:
        return (
          <div className={styles.columnHeader}>
            <div className={styles.headerContent}>
              <IonLabel
                className={classNames(styles.generalTableTitle, styles.columnTruncate)}
              >
                {resolveDashboardName(column)}
              </IonLabel>
            </div>
          </div>
        );
      case DashboardColumnsEnum.PRODUCTS_NUM:
        return (
          <div className={styles.columnHeader}>
            <div className={styles.headerContent}>
              <IonLabel
                style={{
                  maxWidth: colWidths[index],
                }}
                className={classNames(styles.generalTableTitle, styles.columnTruncate)}
              >
                {resolveDashboardName(column)}
              </IonLabel>
            </div>
          </div>
        );
      case DashboardColumnsEnum.PROCESS_TYPE:
        return (
          <div className={styles.columnHeader}>
            <div className={styles.headerContent}>
              <IonLabel
                style={{
                  maxWidth: colWidths[index],
                }}
                className={classNames(styles.generalTableTitle, styles.columnTruncate)}
              >
                {resolveDashboardName(column)}
              </IonLabel>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if ((!isDesktopLarge && !isTablet) || loadingData) {
    return null;
  }

  return (
    <div className={styles.tableHeadContainer} ref={tableHeadRef}>
      <table>
        <thead>
          <tr className={styles.generalTableRow}>
            {dashboardColumns.map((column, index) => (
              <th key={column.id} style={{ maxWidth: colWidths[index], minWidth: colWidths[index] }}>
                {renderColumnHeader(column.id, index)}
              </th>
            ))}
          </tr>
        </thead>
      </table>
    </div>
  );
}
